import { Box, IconButton, Skeleton, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LOGGED_IN_HOME_PAGE, MAX_NAVBAR_WIDTH } from "../config";
import { isParamNumeric } from "../utils/utils";
import { YouthKidCard } from "../youth-kid/YouthKidCard";
import { AppPage } from "../utils/AppPage";
import { NoItemCard } from "../utils/NoItemCard";
import { TeamCard } from "./TeamCard";
import { TeamAddYouthKidModal } from "./TeamAddYouthKidModal";
import { ManageItemsWidget } from "../utils/ManageItemsWidget";
import { useGetTeamById } from "./useGetTeamById";
import { useGetYouthKidsByTeam } from "../youth-kid/useGetYouthKidsByTeam";
import { TeamDeleteModal } from "./TeamDeleteModal";
import { MdPeople } from "react-icons/md";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { TeamLeaderManageModal } from "./TeamLeaderManageModal";

export const TeamPage = () => {
  const {
    isOpen: isAddOpen,
    onClose: onAddClose,
    onOpen: onAddOpen,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onClose: onDeleteClose,
    onOpen: onDeleteOpen,
  } = useDisclosure();
  const {
    isOpen: isLeaderOpen,
    onClose: onLeaderClose,
    onOpen: onLeaderOpen,
  } = useDisclosure();
  const { teamId } = useParams();
  const [team, refetch] = useGetTeamById(teamId);
  const { youthKids, queryYouthKids } = useGetYouthKidsByTeam(team?.id);
  const navigate = useNavigate();

  // Navigate if invalid
  useEffect(() => {
    if (!isParamNumeric(teamId)) {
      navigate(LOGGED_IN_HOME_PAGE);
    }
  }, [navigate, teamId]);

  /*
   * On add youth kid success
   */
  const onYouthKidAddSuccess = useCallback(() => {
    onAddClose();
    queryYouthKids(true);
  }, [onAddClose, queryYouthKids]);

  // Do nothing if invalid
  if (!isParamNumeric(teamId) || !team) {
    return null;
  }

  return (
    <AppPage title={`Team ${team?.name ?? ""}`}>
      {team ? <TeamCard team={team} clickable={false} /> : <Skeleton />}
      <ManageItemsWidget
        title="Team Members"
        onNewItemClick={onAddOpen}
        onEditClick={() => navigate(`/team/${teamId}/edit`)}
        onDeleteClick={onDeleteOpen}
      >
        <IconButton
          onClick={onLeaderOpen}
          colorScheme={THEME_COLOR_SCHEME}
          icon={<MdPeople />}
          aria-label={"manage people"}
          variant={"outline"}
          marginRight={2}
        />
      </ManageItemsWidget>
      <TeamAddYouthKidModal
        teamId={team.id}
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSuccess={onYouthKidAddSuccess}
      />
      <TeamDeleteModal
        team={team}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
      />
      <TeamLeaderManageModal
        team={team}
        isOpen={isLeaderOpen}
        onClose={onLeaderClose}
        onSuccess={refetch}
      />
      <Box
        display={"flex"}
        flexDir={"column"}
        width={"100%"}
        maxW={MAX_NAVBAR_WIDTH}
      >
        {youthKids.length > 0 ? (
          youthKids.map((youthKid) => (
            <YouthKidCard key={youthKid.id} youthKid={youthKid} />
          ))
        ) : (
          <NoItemCard
            buttonText={"Add youth kid"}
            description={`There are no youth kids in team ${team.name}`}
            onButtonClick={onAddOpen}
          />
        )}
      </Box>
    </AppPage>
  );
};
